import { useMemo } from 'react';
import { Header, GridColumn, Grid, Button, Icon } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { DeploymentTypes } from '~/constants/deployment-type';
import PricingEstimate from './PricingEstimate';
import KeyDetails from './KeyDetails';
import ProductionEstimate from './ProductionEstimate';
import BudgetTable from './BudgetTable';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { useDataroomQuery } from '~/requests/dataroom/useDataroomQuery';
import { getDataroomCounts } from '~/utils/calculators/dataroomCounts';
import { Project } from '~/types/project';
import ProjectCompletionStates from './ProjectCompletionStates';
import AdminNotesForm from '~/features/admin/AdminControlPanel/AdminProjectForms/AdminNotes';
import ProjectRejection from '~/features/admin/AdminControlPanel/AdminProjectForms/ProjectRejection';
import NotesForm from '~/components/form/NotesForm';
import ProjectChecklist from './ProjectChecklist';
import { DealStates } from '~/constants';

import '../styles.scss';

import SummaryDocumentButton from '~/components/SummaryDocumentButton';
import DealSpreadsheetButton from '~/components/DealSpreadsheetButton';
import GenerateCimButton from '~/components/GenerateCimButton';

interface ProjectSummaryDataProps {
  project: Project;
  values: any;
  projectErrors: any;
  projectState: any;
  projectAutopricing: any;
}

export default function ProjectSummaryData({
  project,
  values: projectDisplayValues,
  projectErrors,
  projectState,
  projectAutopricing
}: ProjectSummaryDataProps) {
  const { permissions } = usePermissions();
  const { data: dataroom } = useDataroomQuery({ onError() { /* user may lack dataroom access */ } });
  const dataroomCounts = useMemo(() => (dataroom ? getDataroomCounts(dataroom.documents) : null), [dataroom]);
  const isInvestor = permissions?.hasProjectInvestorAccess && !permissions.isAdmin;
  const showDataroomLink = permissions?.canReadDataroom && !(isInvestor && projectState?.dealState < DealStates.IN_DILIGENCE && !dataroomCounts?.uploaded);
  const showProductionEstimateTable = permissions.hasProjectDeveloperAccess || projectDisplayValues?.productionEstimates?.hasProductionEstimate;
  const showPricingEstimate = (
    !projectState?.isITC &&
    !project.hasStorage &&
    project?.deploymentType !== DeploymentTypes.COMMUNITY_SOLAR &&
    !projectState?.isEpcOperating
  );
  const showProjectRejection = permissions?.isAdmin
    && projectState?.dealState >= DealStates.IN_REVIEW
    && projectState?.dealState < DealStates.IN_DILIGENCE;
  return (
    <>
      {projectDisplayValues ? (
        <Grid stackable>
          <GridColumn width={8}>

            {permissions?.isInvestor && <ProjectCompletionStates project={project} />}

            {permissions?.isInvestor && <ProjectChecklist />}

            {!permissions?.isInvestor && <ProjectCompletionStates project={project} />}

            <div className="card">
              <Header as='h2'>Overview</Header>
              <KeyDetails project={project} values={projectDisplayValues} isEpcOperating={projectState?.isEpcOperating} />
            </div>

            <div className="card">
              <Header as='h2'>Budget</Header>
              <BudgetTable
                project={project}
                viewMode='summary'
              />
            </div>
            <div className="card">
              <Header as='h2'>Additional Notes</Header>
              <NotesForm />
            </div>

            {permissions?.isAdmin && (
              <div className="card">
                <Header as='h2'>Admin Notes</Header>
                <AdminNotesForm />
              </div>
            )}

            {showProjectRejection && (
              <div className="card">
                <Header as='h2'>Return to Draft</Header>
                <ProjectRejection />
              </div>
            )}
          </GridColumn>
          <GridColumn width={4}>

            {(permissions?.isInvestor || permissions?.isAdmin) && (
              <div id="key-downloads" className="card">
                <Header as='h2'>Key Downloads</Header>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px' }}>
                  <SummaryDocumentButton />
                  <DealSpreadsheetButton />
                  {showDataroomLink && (
                    <Button as={Link} to='dataroom' className="outlined secondary" size="small">
                      <Icon name='folder open' />
                      Go to data room
                    </Button>
                  )}
                </div>
              </div>
            )}

            {permissions?.isAdmin && project?.id && !project?.portfolioId && (
              <div className="card">
                <div style={{ display: 'block', backgroundColor: '#CC0000', padding: '4px 6px', borderRadius: '4px', color: 'white', textAlign: 'center', fontSize: '12px', fontWeight: 'bold', marginBottom: '12px' }}>Admin only</div>
                <Header as='h2' style={{ marginBottom: '1rem' }}>CIM Generator</Header>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px' }}>
                  <GenerateCimButton dealType='project' dealId={project.parentId ?? project.id} />
                </div>
              </div>
            )}


            {((permissions.hasProjectDeveloperAccess && showPricingEstimate) || permissions.isAdmin) && (
              <div className="pricing-estimate-container">
                <div className="card">
                  {!showPricingEstimate && permissions.isAdmin && <div style={{ display: 'block', backgroundColor: '#CC0000', padding: '4px 6px', borderRadius: '4px', color: 'white', textAlign: 'center', fontSize: '12px', fontWeight: 'bold', marginBottom: '12px' }}>Admin only</div>}
                  <PricingEstimate
                    project={project}
                    projectState={projectState}
                    projectAutopricing={projectAutopricing}
                    projectErrors={projectErrors}
                  />
                </div>
              </div>
            )}

            {showProductionEstimateTable && (
              <div className="production-estimate-container">
                <div className="card">
                  <ProductionEstimate
                    projectDisplayValues={projectDisplayValues}
                    project={project}
                    projectErrors={projectErrors}
                  />
                </div>
              </div>
            )}

          </GridColumn>
        </Grid>
      ) : (
        <h2>No data</h2>
      )}
    </>

  );
}